import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, Download, ClipboardList, ArrowRight } from "lucide-react";

const QUOTE_STATUS = {
  pendiente: "bg-orange-500/15 text-orange-500 border-orange-500/30",
  en_revision: "bg-blue-500/15 text-blue-500 border-blue-500/30",
  aprobada: "bg-green-500/15 text-green-500 border-green-500/30",
  rechazada: "bg-red-500/15 text-red-500 border-red-500/30",
};

export default function UserDashboard() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [tab, setTab] = useState("quotes");

  useEffect(() => {
    api.get("/my/quotes").then((r) => setQuotes(r.data));
    api.get("/my/contracts").then((r) => setContracts(r.data));
  }, []);

  const download = (c) => {
    window.open(`${API}/contracts/${c.id}/pdf`, "_blank");
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="bg-[#0A0A0A] text-white grain">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-14">
          <div className="overline text-brand mb-2">Mi cuenta</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">HOLA, {user?.name?.split(" ")[0]?.toUpperCase()}</h1>
          <p className="text-white/60 mt-2">Gestiona tus cotizaciones y contratos.</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10">
        <div className="flex gap-2 mb-8 border-b border-border">
          <button onClick={() => setTab("quotes")} data-testid="tab-quotes"
            className={`px-5 py-3 font-semibold text-sm border-b-2 -mb-px transition-colors ${tab === "quotes" ? "border-brand text-foreground" : "border-transparent text-muted-foreground"}`}>
            <ClipboardList size={16} className="inline mr-2" />Mis cotizaciones ({quotes.length})
          </button>
          <button onClick={() => setTab("contracts")} data-testid="tab-contracts"
            className={`px-5 py-3 font-semibold text-sm border-b-2 -mb-px transition-colors ${tab === "contracts" ? "border-brand text-foreground" : "border-transparent text-muted-foreground"}`}>
            <FileText size={16} className="inline mr-2" />Mis contratos ({contracts.length})
          </button>
        </div>

        {tab === "quotes" && (
          quotes.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              Aún no tienes cotizaciones. <Link to="/catalogo" className="text-brand font-semibold">Explora el catálogo <ArrowRight size={14} className="inline" /></Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="user-quotes">
              {quotes.map((q) => (
                <div key={q.id} className="flex flex-wrap items-center justify-between gap-4 border border-border p-5">
                  <div>
                    <div className="font-display font-bold text-xl tracking-tight">{q.machinery_name}</div>
                    <div className="text-sm text-muted-foreground">{q.listing_type === "venta" ? "Compra" : "Alquiler"} · {new Date(q.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 border ${QUOTE_STATUS[q.status]}`}>{q.status.replace("_", " ").toUpperCase()}</span>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "contracts" && (
          contracts.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">Aún no tienes contratos generados.</div>
          ) : (
            <div className="space-y-3" data-testid="user-contracts">
              {contracts.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center justify-between gap-4 border border-border p-5">
                  <div>
                    <div className="overline text-brand">{c.contract_number}</div>
                    <div className="font-display font-bold text-xl tracking-tight">{c.machinery_name}</div>
                    <div className="text-sm text-muted-foreground">{c.type === "venta" ? "Compraventa" : "Alquiler"} · {c.currency} {c.amount?.toLocaleString()} · {c.status}</div>
                  </div>
                  <button onClick={() => download(c)} data-testid={`download-contract-${c.id}`}
                    className="flex items-center gap-2 px-5 py-3 bg-brand text-black font-semibold hover:bg-brand-hover transition-colors">
                    <Download size={16} /> Descargar PDF
                  </button>
                </div>
              ))}
            </div>
          )
        )}
      </div>
      <Footer />
    </div>
  );
}
